import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import {
  FaCreditCard,
  FaShoppingCart,
  FaNewspaper,
  FaFileInvoiceDollar,
  FaTruck,
  FaShieldAlt,
  FaPhoneAlt, FaExchangeAlt
} from "react-icons/fa";

const TAG = ({ children }) => <span className="tech-tag">{children}</span>;
const INITIAL_VISIBLE = 4;

function CaseModal({ show, onHide, item }) {
  if (!item) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="case-modal">
      <Modal.Header closeButton>
        <Modal.Title>{item.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="case-meta">
          <div><b>Context:</b> {item.industry}</div>
          <div><b>Period:</b> {item.period}</div>
          <div><b>Team setup:</b> {item.team}</div>
          <div><b>My role:</b> {item.role}</div>
        </div>

        <hr />

        <h6>What was built</h6>
        <ul>
          {item.built.map((x, i) => <li key={i}>{x}</li>)}
        </ul>

        <h6>My contributions</h6>
        <ul>
          {item.contributions.map((x, i) => <li key={i}>{x}</li>)}
        </ul>

        {item.impact?.length ? (
          <>
            <h6>Outcome</h6>
            <ul>
              {item.impact.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
          </>
        ) : null}

        <div className="case-tags">
          {item.tech.map((t) => <TAG key={t}>{t}</TAG>)}
        </div>

        <div className="small text-muted mt-3">
          Case studies are presented without client-sensitive details. Architecture walkthrough available on request.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        <Button variant="primary" href="#contact" onClick={onHide}>Ask for details</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function SystemsSection() {
  const [selected, setSelected] = useState(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const items = useMemo(() => ([
    {
      id: "fintech-payments",
      title: "Corporate Payments Authorization Platform",
      industry: "FinTech / Corporate payments (issuer side)",
      period: "2021 - present",
      team: "Multiple backend and operations teams in distributed production setup",
      role: "Senior software engineer (freelance)",
      built: [
        "Highly available issuer-side real-time credit-card authorization system",
        "Real-time processing and routing of high-volume international authorization traffic",
        "Synchronous low-latency integrations to external issuer-processing platforms and booking systems",
      ],
      contributions: [
        "Further development and stabilization of business-critical 24/7 authorization flows (live production)",
        "Analysis and resolution of critical production incidents; root-cause analysis across network, processor and internal service boundaries",
        "Migration of authorization services from on-prem infrastructure to containerized Kubernetes environment on Azure",
        "Built internal operational/support tooling (incl. UI) for authorization-flow analysis and support efficiency",
      ],
      impact: [
        "Reduced incident pressure and improved stability of authorization traffic under production load",
        "Faster triage through improved operational tooling and better system visibility",
      ],
      tech: ["Java", "Spring Boot", "Kafka", "TCP/Netty", "Docker", "Kubernetes", "Azure", "MongoDB", "Grafana"],
      icon: FaCreditCard,
    },
    {
      id: "retail-oms",
      title: "Omnichannel Order Management Platform",
      industry: "Retail / E-commerce",
      period: "2020 - 2021",
      team: "Backend product team inside large omnichannel program",
      role: "Senior software engineer (freelance, embedded)",
      built: [
        "Distributed order-management backend services for validation and availability decisions",
        "Available-to-Promise (ATP) decision logic for real-time order validation using distributed inventory data",
        "Online reservations and cross-channel stock handling with enterprise OMS integration",
      ],
      contributions: [
        "Implemented core backend services for order validation, reservations and cross-channel stock handling",
        "Integrated with enterprise retail systems (IBM Sterling OMS) and surrounding platform services",
        "Improved reliability and operability of event-driven flows for peak transaction periods",
      ],
      impact: [
        "Improved reliability of reservation and availability updates during peak periods",
      ],
      tech: ["Java", "Kotlin", "Spring Boot", "Kafka", "Kubernetes", "Docker", "GCP", "PostgreSQL", "MongoDB"],
      icon: FaShoppingCart,
    },
    {
      id: "publishing-platform",
      title: "High-Traffic Publishing and Recommendation Platform",
      industry: "Digital publishing",
      period: "2019 - 2020",
      team: "Platform + data/ML engineers",
      role: "Senior software engineer (freelance)",
      built: [
        "High-traffic publishing backend with real-time personalized content delivery",
        "Recommendation + experimentation (A/B testing) support services",
        "Streaming pipelines processing large volumes of user interaction events",
      ],
      contributions: [
        "Developed backend services supporting personalization, experimentation and analytics workflows",
        "Implemented/maintained streaming-based event processing used for recommendation and product experimentation",
        "Improved production readiness: operability, rollout support and observability for critical services",
      ],
      impact: [
        "More robust experimentation and recommendation delivery in production",
      ],
      tech: ["Java", "Python", "Kafka", "Kubernetes", "Docker", "AWS", "Elasticsearch"],
      icon: FaNewspaper,
    },
    {
      id: "fintech-billing",
      title: "Credit Card Billing and Statements Platform",
      industry: "FinTech / Billing",
      period: "2018 - 2019",
      team: "Backend domain team with enterprise integrations",
      role: "Senior software engineer (freelance)",
      built: [
        "Credit-card billing and statement generation services",
        "Aggregation of transaction data from SAP and CRM systems",
        "PDF output generation integration via enterprise output management (Papyrus)",
      ],
      contributions: [
        "Implemented event-driven processing of financial transaction/billing data",
        "Built robust integrations to SAP/CRM source systems and downstream output management (Papyrus) for PDF generation",
        "Improved production reliability and predictability of statement generation workflows",
      ],
      impact: [
        "Stable billing pipeline and predictable statement generation cycles",
      ],
      tech: ["Java", "Spring Boot", "Kafka", "MongoDB", "Docker", "Kubernetes", "Azure"],
      icon: FaFileInvoiceDollar,
    },
    {
      id: "fulfillment",
      title: "E-Commerce Fulfillment and Logistics Services",
      industry: "E-commerce / Logistics",
      period: "2017 - 2018",
      team: "Cloud-native product team",
      role: "Senior software engineer (freelance)",
      built: [
        "Backend services for automated warehouse and order-fulfillment processes",
        "Event-driven order/status processing and synchronization across systems",
        "Operational monitoring setup for live logistics processes",
      ],
      contributions: [
        "Implemented event-driven order and status processing services for fulfillment flows",
        "Synchronized stock and order state between shop and logistics systems (consistency + reliability)",
        "Improved operability with monitoring/metrics (Prometheus/Grafana) to support production operations",
      ],
      impact: [
        "Better consistency of stock/order state and smoother fulfillment execution",
      ],
      tech: ["Java", "Spring Boot", "Kafka", "Kubernetes", "Docker", "MySQL", "GCP"],
      icon: FaTruck,
    },
    {
      id: "fraud-analytics",
      title: "Risk and Fraud Analytics",
      industry: "FinTech / Risk",
      period: "2017",
      team: "Innovation Lab (data + engineering)",
      role: "Machine Learning Engineer",
      built: [
        "Machine-learning-based fraud detection components",
        "Data processing workflows for structured and unstructured financial signals",
        "Model usage in production-oriented risk workflows",
      ],
      contributions: [
        "Developed and evaluated fraud/anomaly detection approaches using financial data signals",
        "Supported training and production usage of ML models (practical integration into workflows)",
        "Worked with domain experts on usability/explainability aspects for operational decision support",
      ],
      impact: [
        "Improved detection capability in risk-related decision support",
      ],
      tech: ["Python", "Java", "Machine Learning", "PostgreSQL"],
      icon: FaShieldAlt,
    },
    ,

    // Optional but recommended: add your 2014–2017 consulting block from CV for completeness
    {
      id: "tng-consulting",
      title: "Enterprise Integration & Modernization (Insurance & Telecom)",
      industry: "IT Consulting / Insurance & Telecommunications",
      period: "2014 - 2017",
      team: "Multiple enterprise project teams (consulting)",
      role: "Software Consultant",
      built: [
        "Claims management backend system (insurance domain)",
        "Migration/modernization of telecom CRM platform",
        "Event-processing and billing components for telecom usage data",
      ],
      contributions: [
        "Backend development and modernization of enterprise systems (Java/Spring/JavaEE)",
        "Event-processing and billing pipelines (incl. Flink) handling high-volume usage data",
        "Integration work across legacy and modernized components with strong production focus",
      ],
      impact: [
        "Modernized critical business systems and improved maintainability/integration reliability",
      ],
      tech: ["Java", "Spring", "JavaEE", "Apache Flink", "Oracle DB", "PostgreSQL", "Hadoop"],
      icon: FaExchangeAlt,
    },
  ]), []);

  const visibleItems = items.slice(0, visibleCount);

  return (
    <section id="projects" className="systems-section">
      <div className="container py-5">
        <div className="text-center mb-4">
          <h2 className="text-uppercase">Selected project experience</h2>
          <p className="text-muted mb-0">
            Case-style snapshots focused on context, contribution and measurable operational outcome.
          </p>
        </div>

        <div className="systems-grid">
          {visibleItems.map((it) => (
            <div key={it.id} className="system-card">
              <div className="system-card-head">
                <div className="system-title-row">
                  <it.icon className="system-title-icon" aria-hidden="true" />
                  <div className="system-title">{it.title}</div>
                </div>
                <div className="system-sub">{it.industry} | {it.period}</div>
              </div>

              <ul className="system-bullets">
                {it.built.slice(0, 3).map((x, i) => <li key={i}>{x}</li>)}
              </ul>

              <div className="system-tags">
                {it.tech.slice(0, 7).map((t) => <TAG key={t}>{t}</TAG>)}
              </div>

              <div className="system-actions">
                <button className="btn btn-sm btn-outline-light" onClick={() => setSelected(it)}>
                  View details
                </button>
                <a className="btn btn-sm btn-light" href="#contact">
                  Contact
                </a>
              </div>
            </div>
          ))}
        </div>

        {visibleCount < items.length ? (
          <div className="text-center mt-4">
            <button className="btn btn-outline-light" onClick={() => setVisibleCount((v) => Math.min(v + 3, items.length))}>
              Load more projects
            </button>
          </div>
        ) : null}

        <CaseModal show={!!selected} onHide={() => setSelected(null)} item={selected} />
      </div>
    </section>
  );
}
